/*
 * Left sidebar for course selection, assignment selection, and recent chats.
 *
 * It keeps the currently viewed session aligned with the surrounding course
 * context so retrieval stays scoped correctly when a user resumes older chats.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import APIService from "../APIService";
import { buildSidebarQuestionLog } from "../utils/chatSessions";
import { useSessionContext } from "../context/SessionContext";

function Sidebar({ 
  questionLog, 
  setQuestionLog, 
  activeChat, 
  setActiveChat,
  selectedCourseId,
  setSelectedCourseId,
  selectedAssignmentId,
  setSelectedAssignmentId,
  selectedAssignmentName,
  setSelectedAssignmentName,
}) {
 const {
  sessions,
  refreshSessions,
  setSelectedSessionId,
  fetchMessages,
 } = useSessionContext();
 const [selectedTutor, setSelectedTutor] = useState("");

 const [availableCourses, setAvailableCourses] = useState([]);
 const [availableAssignments, setAvailableAssignments] = useState([]);

 const handleCourseSelect = async (selected) => {
    if (selected === selectedTutor) return;

    setActiveChat(null);
    setSelectedTutor(selected);
    setSelectedCourseId(selected);
    setSelectedAssignmentId("");
    setSelectedAssignmentName("");
    
    try {
      await APIService.SelectCourse(selected);
      const assignments = await APIService.GetAssignments(selected).catch(() => []);
      setAvailableAssignments(assignments);
    } catch (err) {
      console.error("Failed to select a course:", err);
      alert("Unable to load this course. Please try again.");
    }
 };

 const handleAssignmentChange = async (selectedId) => {
  const assignment = availableAssignments.find(
    (item) => String(item.assignmentId) === String(selectedId)
  );

  setSelectedAssignmentId(selectedId);
  setSelectedAssignmentName(assignment?.title || "");

  if (!selectedTutor) {
    return;
  }

  // Switching assignment context starts a fresh thread so history stays honest.
  setActiveChat(null);
 };

const handleChatClick = useCallback(async (sessionId) => {
  setSelectedSessionId(Number(sessionId));
  
  for (const date in questionLog) {
    const chat = questionLog[date].find(c => c.sessionId === Number(sessionId));
    if (chat) {
      setSelectedCourseId(chat.courseId || chat.courseCovered || "");
      setSelectedTutor(chat.courseId || chat.courseCovered || "");
      setSelectedAssignmentId(chat.assignmentId ? String(chat.assignmentId) : "");
      setSelectedAssignmentName(chat.assignmentName || "");
      break;
    }
  }

  try {
    await fetchMessages(sessionId);
  } catch (error) {
    console.error("Failed to fetch messages for chat:", error);
  }
 }, [
  fetchMessages,
  questionLog,
  setSelectedSessionId,
  setSelectedAssignmentId,
  setSelectedAssignmentName,
  setSelectedCourseId,
 ]);

const fetchCourses = useCallback(async () => {
  try {
    const courses = await APIService.GetCourses(false, true);
    const normalized = Array.isArray(courses)
      ? courses
          .filter((course) => {
            if (typeof course === "string") {
              return true;
            }

            const status = String(course?.status || "").toUpperCase();
            const hasStatus = status.length > 0;
            const isExplicitlyInactive = course?.isActive === false || course?.isActive === 0;

            if (status === "TEST" || status === "ARCHIVED") {
              return false;
            }

            // The backend may send either explicit status or legacy `isActive`.
            if (hasStatus) {
              return status === "ACTIVE" && !isExplicitlyInactive;
            }

            return !isExplicitlyInactive;
          })
          .map((course) => {
            if (typeof course === "string") {
              return {
                courseId: course,
                name: course,
              };
            }

            return {
              courseId: course.courseId || "Unknown",
              name: course.name || course.courseId || "Unknown",
            };
          })
      : [];
    setAvailableCourses(normalized);
  } catch (err) {
    console.error("Error loading courses:", err);
  }
}, []);

const recentChats = useMemo(() => (
  Object.values(questionLog)
    .flat()
    .sort((left, right) => Number(right.sessionId) - Number(left.sessionId))
), [questionLog]);

const syncSelectedCourseAssignments = useCallback(async (courseId) => {
  if (!courseId) {
    setAvailableAssignments([]);
    return;
  }

  try {
    const assignments = await APIService.GetAssignments(courseId).catch(() => []);
    setAvailableAssignments(assignments);
  } catch (error) {
    console.error("Failed to fetch assignments for selected course:", error);
  }
}, []);

useEffect(() => {
  setQuestionLog(buildSidebarQuestionLog(sessions));
}, [sessions, setQuestionLog]);

 useEffect(() => {
   if (!activeChat) {
     return;
   }

   let activeConversation = null;
   for (const date in questionLog) {
     const found = questionLog[date].find((conversation) => conversation.sessionId === Number(activeChat));
     if (found) {
       activeConversation = found;
       break;
     }
   }

   if (!activeConversation) {
     return;
   }

   // Opening an existing chat should also restore its course/assignment selectors.
   if (activeConversation.courseId || activeConversation.courseCovered) {
     const courseId = activeConversation.courseId || activeConversation.courseCovered;
     setSelectedCourseId(courseId);
     setSelectedTutor(courseId);
     syncSelectedCourseAssignments(courseId);
   }

   setSelectedAssignmentId(activeConversation.assignmentId ? String(activeConversation.assignmentId) : "");
   setSelectedAssignmentName(activeConversation.assignmentName || "");

   handleChatClick(activeConversation.sessionId);
 }, [
   activeChat,
   handleChatClick,
   questionLog,
   setSelectedAssignmentId,
   setSelectedAssignmentName,
   setSelectedCourseId,
   setSelectedTutor,
   syncSelectedCourseAssignments,
 ]);

 useEffect(() => {
   fetchCourses();
  }, [fetchCourses]);

 useEffect(() => {
   if (sessions.length > 0) {
     return;
   }

   refreshSessions().catch((err) => {
     console.error("Error loading chats:", err);
   });
 }, [refreshSessions, sessions.length]);

 return (
   <aside className="sidebar" aria-label="Chat sidebar">
       <div className="sidebar-scroll">
         <div className="sidebar-section">
           <label className="sidebar-field-label" htmlFor="tutor-dropdown">
             Course
           </label>
           <select
             id="tutor-dropdown"
             value={selectedTutor}
             onChange={(event) => handleCourseSelect(event.target.value)}
             className="tutor-dropdown modern-select"
             aria-label="Select course"
           >
             <option value="" disabled>Select a course</option>
             {availableCourses.map((course) => (
               <option key={course.courseId} value={course.courseId}>
                 {course.courseId} - {course.name || course.courseId}
               </option>
             ))}
           </select>
         </div>

         <div className="sidebar-section">
           <label className="sidebar-field-label" htmlFor="assignment-dropdown">
             Assignment
           </label>
           <select
             id="assignment-dropdown"
             value={selectedAssignmentId}
             onChange={(event) => handleAssignmentChange(event.target.value)}
             className="tutor-dropdown modern-select"
             disabled={!selectedTutor}
             aria-label="Select assignment or topic"
           >
             <option value="">General course help</option>
             {availableAssignments.map((assignment) => (
               <option key={assignment.assignmentId} value={assignment.assignmentId}>
                 {assignment.assignmentNumber ? `#${assignment.assignmentNumber} ` : ""}
                 {assignment.title}
               </option>
             ))}
           </select>
         </div>

         <div className="sidebar-section history-section sidebar-chat-section">
           <div className="sidebar-section-title">Recent Chats</div>
           <div className="sidebar-button-list" role="list" aria-label="Recent chats">
             {recentChats.map((conversation) => {
               const sessionId = conversation.sessionId;
               return (
                 <button
                   key={sessionId}
                   type="button"
                   onClick={() => handleChatClick(sessionId)}
                   className={`sidebar-item ${activeChat === sessionId ? "sidebar-active-chat" : ""}`}
                   aria-current={activeChat === sessionId ? "page" : undefined}
                   aria-label={`Open chat for ${conversation.assignmentName || conversation.courseId || "general help"} in ${conversation.courseId || "course context"}`}
                 >
                   <span className="sidebar-item-title">
                     {conversation.assignmentName || conversation.courseId || "General help"}
                   </span>
                   <span className="sidebar-item-subtitle">
                     {conversation.courseId || "Course"} · {conversation.prompt}
                   </span>
                 </button>
               );
             })}
             {recentChats.length === 0 && (
               <div className="sidebar-empty-state">No chats yet</div>
             )}
           </div>
         </div>
       </div>

   </aside>
 );
}

export default Sidebar;
